import {CheckoutSettingsModel} from '../../../../domain/models/checkout/checkoutSettings/CheckoutSettings.model';
import {
  AddressTarget,
  FieldPropertiesByTarget,
  FormFieldTarget,
  BillingFormOverrides,
} from '../../../../types/checkoutApp.types';
import {
  getExcludeGoogleAutoCompleteOverrides,
  getSettingsOverrides,
  mergeOverrides,
  getLabelsOverrides,
  mandatoryOverride,
  getAddressFormInitialState,
  getContactFormInitialState,
  hiddenOverride,
} from '../form.utils';
import {ILocaleKeys} from '../../../../locale-keys/LocaleKeys';
import {ContactModel} from '../../../../domain/models/checkout/Cart/Contact.model';
import {AddressModel} from '../../../../domain/models/checkout/Cart/Address.model';
import {FormValues, VatIDValue} from '@wix/form-fields';
import {type CountryCode} from '@wix/form-multiline-address';
import {isCountryWithVat} from '../../../../domain/utils/isCountryWithVat';
import {WixCommonVatType} from '../../../../gql/graphql';
import {CashierMandatoryField, CashierMandatoryFieldsOverrides} from '../../../../types/payment.types';
import {FormOverride} from '@wix/form-viewer/dist/types/types';
import {VatIdModel} from '../../../../domain/models/checkout/Cart/VatId.model';
import {getVatType} from '../../../../domain/utils/vat.util';
import {isCheckoutEligibleReduceFields} from '../../../../domain/utils/isCheckoutEligibleReduceFields.utils';
import {AddressWithContactModel} from '../../../../domain/models/checkout/Cart/AddressWithContact.model';

type CashierAddressField = Extract<
  CashierMandatoryField,
  'address' | 'zipCode' | 'countryCode' | 'houseNumber' | 'street' | 'state' | 'city'
>;

const cashierFieldToAddressTarget: Record<CashierAddressField, AddressTarget> = {
  [CashierMandatoryField.address]: AddressTarget.addressLine,
  [CashierMandatoryField.zipCode]: AddressTarget.postalCode,
  [CashierMandatoryField.countryCode]: AddressTarget.country,
  [CashierMandatoryField.houseNumber]: AddressTarget.streetNumber,
  [CashierMandatoryField.street]: AddressTarget.streetName,
  [CashierMandatoryField.state]: AddressTarget.subdivision,
  [CashierMandatoryField.city]: AddressTarget.city,
};

const cashierFieldToAddressTargetOld = {
  [CashierMandatoryField.address]: AddressTarget.address_line,
  [CashierMandatoryField.zipCode]: AddressTarget.postal_code,
  [CashierMandatoryField.countryCode]: AddressTarget.country,
  [CashierMandatoryField.houseNumber]: AddressTarget.street_number,
  [CashierMandatoryField.street]: AddressTarget.street_name,
  [CashierMandatoryField.state]: AddressTarget.subdivision,
  [CashierMandatoryField.city]: AddressTarget.city,
};

function mapCashierMandatoryFieldToOverride(
  cashierMandatoryField: CashierMandatoryField,
  mandatory?: boolean,
  doesFormHaveMultilineAddress?: boolean
): BillingFormOverrides | undefined {
  /* istanbul ignore next */
  if (!mandatory) {
    return {};
  }
  switch (cashierMandatoryField) {
    case CashierMandatoryField.address:
    case CashierMandatoryField.city:
    case CashierMandatoryField.countryCode:
    case CashierMandatoryField.houseNumber:
    case CashierMandatoryField.state:
    case CashierMandatoryField.street:
    case CashierMandatoryField.zipCode: {
      const target = doesFormHaveMultilineAddress
        ? cashierFieldToAddressTarget[cashierMandatoryField]
        : cashierFieldToAddressTargetOld[cashierMandatoryField];
      return {
        address: {
          [target]: mandatoryOverride,
        },
      };
    }
    case CashierMandatoryField.phone:
      return {
        phone: mandatoryOverride,
      };
    /* istanbul ignore next */
    default:
      /* istanbul ignore next */
      return undefined;
  }
}

function getPhoneHiddenOverride(
  mandatoryFields: CashierMandatoryFieldsOverrides,
  isLeanFlow?: boolean,
  phoneValue?: string
): BillingFormOverrides | undefined {
  if (phoneValue || mandatoryFields[CashierMandatoryField.phone] || isLeanFlow) {
    return;
  }
  return {[FormFieldTarget.phone]: hiddenOverride};
}

function getCompanyNameHiddenOverride(
  isLeanFlow?: boolean,
  companyNameValue?: string
): BillingFormOverrides | undefined {
  if (companyNameValue || isLeanFlow) {
    return;
  }
  return {[FormFieldTarget.company_name]: hiddenOverride};
}

function getAddressFieldHiddenOverride(
  mandatoryFields: CashierMandatoryFieldsOverrides,
  fieldTarget: CashierMandatoryField,
  addressValue?: string
): BillingFormOverrides | undefined {
  if (!!addressValue || mandatoryFields[fieldTarget]) {
    return;
  }
  const target = cashierFieldToAddressTarget[fieldTarget as CashierAddressField];
  return {
    [FormFieldTarget.address]: {
      [target]: hiddenOverride,
    },
  };
}

function getFullNameHiddenOveride(
  mandatoryFields: CashierMandatoryFieldsOverrides,
  isLeanFlow?: boolean,
  firstNameValue?: string,
  lastNameValue?: string
): BillingFormOverrides | undefined {
  const hasNameMandatoryField =
    mandatoryFields[CashierMandatoryField.firstName] || mandatoryFields[CashierMandatoryField.lastName];
  const hasNameValue = !!firstNameValue || !!lastNameValue;
  if (hasNameValue || hasNameMandatoryField || isLeanFlow) {
    return;
  }
  return {
    [FormFieldTarget.full_name]: hiddenOverride,
  };
}

function getAddressLineHiddenOveride(
  mandatoryFields: CashierMandatoryFieldsOverrides,
  address: AddressModel | undefined
): BillingFormOverrides | undefined {
  const hasAddressLineValue = !!address?.addressLine || !!address?.streetAddress;
  if (
    hasAddressLineValue ||
    mandatoryFields[CashierMandatoryField.address] ||
    mandatoryFields[CashierMandatoryField.houseNumber] ||
    mandatoryFields[CashierMandatoryField.street]
  ) {
    return;
  }
  return {
    [FormFieldTarget.address]: {
      [AddressTarget.addressLine]: hiddenOverride,
      [AddressTarget.streetName]: hiddenOverride,
      [AddressTarget.streetNumber]: hiddenOverride,
    },
  };
}

export function getCashierMandatoryOverrides({
  mandatoryFields,
  doesFormHaveMultilineAddress,
}: {
  mandatoryFields: CashierMandatoryFieldsOverrides;
  doesFormHaveMultilineAddress?: boolean;
}): BillingFormOverrides {
  const mandatoryCashierFields = Object.keys(mandatoryFields) as (keyof typeof mandatoryFields)[];
  const mandoryOverrides = mandatoryCashierFields.map((field) =>
    mapCashierMandatoryFieldToOverride(field, mandatoryFields[field], doesFormHaveMultilineAddress)
  );
  return mergeOverrides(...mandoryOverrides);
}

export function getCashierHiddenOverrides({
  mandatoryFields,
  isLeanFlow,
  billingInfo,
}: {
  mandatoryFields: CashierMandatoryFieldsOverrides;
  isLeanFlow?: boolean;
  billingInfo?: AddressWithContactModel;
}): BillingFormOverrides {
  const phoneOverride = getPhoneHiddenOverride(mandatoryFields, isLeanFlow, billingInfo?.contact?.phone);
  const companyNameOverride = getCompanyNameHiddenOverride(isLeanFlow, billingInfo?.contact?.company);
  const fullNameOverride = getFullNameHiddenOveride(
    mandatoryFields,
    isLeanFlow,
    billingInfo?.contact?.firstName,
    billingInfo?.contact?.lastName
  );
  const addressOverrides = [
    {target: CashierMandatoryField.city, addressValue: billingInfo?.address?.city},
    {target: CashierMandatoryField.state, addressValue: billingInfo?.address?.subdivision},
    {target: CashierMandatoryField.zipCode, addressValue: billingInfo?.address?.postalCode},
  ].map(({target, addressValue}) => getAddressFieldHiddenOverride(mandatoryFields, target, addressValue));
  const addressLineOverride = getAddressLineHiddenOveride(mandatoryFields, billingInfo?.address);
  return mergeOverrides(...addressOverrides, phoneOverride, addressLineOverride, companyNameOverride, fullNameOverride);
}

function mapFormFieldPropsToOverrides(props: FieldPropertiesByTarget[string]): FormOverride {
  if (!props) {
    return {
      hidden: true,
      required: false,
    };
  }
  /* istanbul ignore else */
  if (typeof props?.hidden === 'boolean' && typeof props?.required === 'boolean') {
    return {
      hidden: props.hidden,
      required: props.required,
    };
  }
  /* istanbul ignore next */
  return {};
}

function getVatFormInitialState(contact?: ContactModel, nullIfMIssing?: boolean) {
  if (nullIfMIssing && !contact?.vatId?.id) {
    return {vat_id: null};
  }

  return {
    vat_id: {
      id: contact?.vatId?.id ?? /* istanbul ignore next */ '',
      type: contact?.vatId?.type ?? /* istanbul ignore next */ WixCommonVatType.UNSPECIFIED,
    },
  };
}

export function getShippingDetailsRelatedFieldsOverrides({
  formFieldsProperties,
  doesFormHaveMultilineAddress,
}: {
  formFieldsProperties: FieldPropertiesByTarget;
  doesFormHaveMultilineAddress?: boolean;
}): BillingFormOverrides {
  const phoneProps = formFieldsProperties[FormFieldTarget.phone];
  const companyProps = formFieldsProperties[FormFieldTarget.company_name];
  const addressProps = formFieldsProperties[FormFieldTarget.address] as FieldPropertiesByTarget | undefined;
  const addressLine2Props = addressProps ? addressProps[AddressTarget.address_line_2] : undefined;

  return {
    phone: mapFormFieldPropsToOverrides(phoneProps),
    company_name: mapFormFieldPropsToOverrides(companyProps),
    ...(doesFormHaveMultilineAddress
      ? {}
      : {
          address: {
            [AddressTarget.address_line_2]: mapFormFieldPropsToOverrides(addressLine2Props),
          },
        }),
  };
}

export function getVatIdOverride(country: CountryCode): BillingFormOverrides {
  return isCountryWithVat(country)
    ? {
        vat_id: {
          hidden: false,
          required: true,
        },
      }
    : {};
}

export function getVatFromVatFormValues(vatFormValues: FormValues | null): VatIdModel | undefined {
  if (!vatFormValues?.vat_id) {
    return undefined;
  }
  const vat = vatFormValues.vat_id as VatIDValue;
  return new VatIdModel({id: vat.id, type: getVatType(vat.type)});
}

export function getBillingFormOverrides({
  cashierMandatoryFields,
  checkoutSettings,
  shippingDetailsFieldsProps,
  excludeGoogleAutoComplete,
  localeKeys,
  country,
  doesFormHaveMultilineAddress,
  supportDynamicBillingFieldsCheckout,
  isMember,
  isLeanFlow,
  hasItemWithBillingTaxAddress,
  billingAddressWithContact,
}: {
  cashierMandatoryFields: CashierMandatoryFieldsOverrides;
  checkoutSettings: CheckoutSettingsModel;
  shippingDetailsFieldsProps: FieldPropertiesByTarget;
  excludeGoogleAutoComplete?: boolean;
  localeKeys?: ILocaleKeys;
  country?: CountryCode | null;
  doesFormHaveMultilineAddress?: boolean;
  supportDynamicBillingFieldsCheckout?: boolean;
  isMember?: boolean;
  isLeanFlow?: boolean;
  hasItemWithBillingTaxAddress?: boolean;
  billingAddressWithContact?: AddressWithContactModel;
}): BillingFormOverrides {
  const showOnlyRequiredCashierFields =
    supportDynamicBillingFieldsCheckout &&
    isCheckoutEligibleReduceFields({
      showRequiredBillingFields: checkoutSettings.showRequiredBillingFields,
      isMember,
      isTaxConfigured: checkoutSettings.isTaxConfigured,
      hasItemWithBillingTaxAddress,
    });

  const cashierMandatoryFieldsOverrides = getCashierMandatoryOverrides({
    mandatoryFields: cashierMandatoryFields,
    doesFormHaveMultilineAddress,
  });
  const cashierHiddenFieldsOverrides = showOnlyRequiredCashierFields
    ? getCashierHiddenOverrides({
        mandatoryFields: cashierMandatoryFields,
        isLeanFlow,
        billingInfo: billingAddressWithContact,
      })
    : undefined;
  const settingsOverrides = !supportDynamicBillingFieldsCheckout
    ? getSettingsOverrides({checkoutSettings, doesFormHaveMultilineAddress})
    : undefined;
  const shippingDetailsRelatedFieldsOverrides = getShippingDetailsRelatedFieldsOverrides({
    formFieldsProperties: shippingDetailsFieldsProps,
    doesFormHaveMultilineAddress,
  });
  const excludeGoogleAutoCompleteOverrides = getExcludeGoogleAutoCompleteOverrides({
    excludeGoogleAutoComplete,
    doesFormHaveMultilineAddress,
    country,
  });
  const labelsOverrides = localeKeys ? getLabelsOverrides({localeKeys}) : /* istanbul ignore next */ {};
  const vatIdOverride = country ? getVatIdOverride(country) : {};

  return mergeOverrides(
    settingsOverrides,
    shippingDetailsRelatedFieldsOverrides,
    excludeGoogleAutoCompleteOverrides,
    labelsOverrides,
    vatIdOverride,
    cashierMandatoryFieldsOverrides,
    cashierHiddenFieldsOverrides
  );
}

export function getBillingFormInitialState({
  contact,
  initialAddress,
  billingContact,
  doesFormHaveMultilineAddress,
}: {
  contact?: ContactModel;
  initialAddress?: AddressModel;
  billingContact?: ContactModel;
  doesFormHaveMultilineAddress?: boolean;
}) {
  const country = initialAddress?.country ?? undefined;

  return {
    ...getContactFormInitialState({
      contact,
      country,
      doesFormHaveMultilineAddress,
    }),
    ...getAddressFormInitialState(initialAddress, country, doesFormHaveMultilineAddress),
    ...getVatFormInitialState(billingContact, true),
  };
}
